import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api from "../api";
import {
  Compass, SpinnerGap, Sparkle, X, CheckCircle, ArrowRight,
} from "@phosphor-icons/react";

export default function DreamsVision() {
  const navigate = useNavigate();
  const [descricao, setDescricao] = useState("");
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [{ data: v }, { data: s }] = await Promise.all([
          api.get("/vision"),
          api.get("/vision/suggestions"),
        ]);
        if (v?.vision?.Descricao) setDescricao(v.vision.Descricao);
        setSuggestions(s.data || []);
      } catch (e) {
        setError(e?.response?.data?.detail || e.message);
      } finally { setLoading(false); }
    })();
  }, []);

  const generate = async () => {
    setGenerating(true); setError(null);
    try {
      const { data } = await api.post("/vision/suggestions/generate", { descricao, replace: true });
      setSuggestions(data.sugestoes || []);
    } catch (e) {
      setError(e?.response?.data?.detail || e.message);
    } finally { setGenerating(false); }
  };

  const accept = async (id) => {
    await api.post(`/vision/suggestions/${id}/accept`);
    setSuggestions(prev => prev.filter(s => s.ID_Sugestao !== id));
  };
  const dismiss = async (id) => {
    await api.post(`/vision/suggestions/${id}/dismiss`);
    setSuggestions(prev => prev.filter(s => s.ID_Sugestao !== id));
  };

  if (loading) return (
    <div className="flex items-center gap-2 text-[#978D78]"><SpinnerGap size={18} className="animate-spin" /> A carregar...</div>
  );

  return (
    <div className="space-y-10 pb-12">
      <header>
        <p className="text-xs uppercase tracking-widest text-[#6F4F1D]/80 mb-2">Passo 1 — Visão</p>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">
          A tua vida de <span className="text-[#6F4F1D]">sonho</span>
        </h1>
        <p className="text-[#4B5563] mt-3 max-w-2xl">
          Descreve a vida que sonhas viver — onde estás, com quem, o que fazes, o que sentes.
          A IA usa isto para te sugerir sonhos concretos.
        </p>
      </header>

      <section className="rounded-2xl border border-[#E6E1D6] bg-white/[0.02] p-6">
        <textarea
          data-testid="vision-textarea"
          value={descricao}
          onChange={(e) => setDescricao(e.target.value)}
          placeholder="Imagina-te 5 anos no futuro. Onde vives? O que fazes ao acordar? Quem te rodeia? O que sentes? Como ganhas a vida? O que aprendes?"
          rows={6}
          className="w-full bg-transparent text-white text-base leading-relaxed placeholder:text-[#6B7280] focus:outline-none resize-y"
        />
        <div className="flex items-center justify-between mt-3">
          <p className="text-[11px] text-[#978D78]">{descricao.length} caracteres · mín. 20</p>
          <button
            onClick={generate}
            disabled={descricao.length < 20 || generating}
            data-testid="generate-suggestions"
            className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-[#6F4F1D] to-fuchsia-500 text-[#0F1829] font-semibold text-sm disabled:opacity-40 hover:shadow-[0_0_30px_rgba(245,158,11,0.3)] transition-all"
          >
            {generating ? <SpinnerGap size={14} className="animate-spin" /> : <Sparkle size={14} weight="fill" />}
            {generating ? "A IA está a sonhar contigo..." : "Gerar sugestões com IA"}
          </button>
        </div>
        {error && <p className="mt-3 text-xs text-red-300" data-testid="vision-error">{error}</p>}
      </section>

      {suggestions.length > 0 && (
        <section>
          <h2 className="text-2xl font-semibold mb-4 flex items-center gap-2">
            <Compass size={20} className="text-[#6F4F1D]" /> Sugestões da IA
            <span className="text-xs font-normal text-[#978D78]">· {suggestions.length} disponíveis</span>
          </h2>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {suggestions.map(s => (
              <article key={s.ID_Sugestao} data-testid={`sugg-${s.ID_Sugestao}`}
                       className="rounded-2xl border border-[#E6E1D6] bg-white/[0.02] p-5 hover:border-[#6F4F1D]/25 transition-colors flex flex-col">
                {s.Categoria && (
                  <span className="text-[10px] uppercase tracking-widest text-[#6F4F1D]/80 mb-2">{s.Categoria}</span>
                )}
                <h3 className="text-base font-semibold leading-snug">{s.Titulo}</h3>
                {s.Descricao && <p className="text-xs text-[#4B5563] mt-2 leading-relaxed">{s.Descricao}</p>}
                {s.DataAlvoSugerida && (
                  <p className="text-[11px] text-[#978D78] mt-3">
                    🗓 Alvo: {new Date(s.DataAlvoSugerida).toLocaleDateString("pt-PT")}
                  </p>
                )}
                <div className="mt-auto pt-4 flex gap-2">
                  <button onClick={() => accept(s.ID_Sugestao)} data-testid={`accept-${s.ID_Sugestao}`}
                          className="flex-1 inline-flex items-center justify-center gap-1 px-3 py-1.5 rounded-full bg-[#6F4F1D] text-[#0F1829] text-xs font-semibold hover:shadow-[0_0_20px_rgba(245,158,11,0.4)]">
                    <CheckCircle size={12} weight="fill" /> Aceitar
                  </button>
                  <button onClick={() => dismiss(s.ID_Sugestao)} data-testid={`dismiss-${s.ID_Sugestao}`}
                          className="px-3 py-1.5 rounded-full border border-white/15 text-xs text-[#4B5563] hover:bg-white">
                    <X size={12} />
                  </button>
                </div>
              </article>
            ))}
          </div>
          <div className="mt-6 flex justify-end">
            <button onClick={() => navigate("/dreams/sonhos")} className="text-sm text-[#6F4F1D] hover:text-white inline-flex items-center gap-1">
              Ir para os meus sonhos <ArrowRight size={14} />
            </button>
          </div>
        </section>
      )}
    </div>
  );
}
